/* eslint-disable no-param-reassign */
import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  map,
  nothing,
  OmniLoadingIndicatorElement,
} from 'omni-ui';

import { om2alert, Om2Dropdown } from 'omni-campaign-ui';
import { produce } from 'immer';

import './campaign-audience-detail-view.js';
import './campaign-audience-cluster-viz.js';
import './campaign-audience-funnel-viz.js';
import './campaign-audience-rfc-legend.js';
import './campaign-audience-segment-grouping.js';

OmniStyleElement.register();
Om2Dropdown.register();
OmniLoadingIndicatorElement.register();

/**
 * @description Omni Campaign Audience Visualization module that helps clients identify Audiences and their stages, the stages segments and role for comms.
 * @element campaign-audience-viz
 * @extends OmniElement
 */
export class CampaignAudienceViz extends OmniElement {
  /**
   * @description Reactive properties of the element
   */
  static get properties() {
    return {
      readOnly: { type: Boolean, attribute: 'read-only', reflect: true },
      audienceView: { type: String },
      audiences: { type: Array },
      stages: { type: Array },
      groups: { type: Object },
      isCluster: { type: Boolean },
      funnelType: { type: String },
      showRFCLegend: { type: Boolean },
      showGroupSegments: { type: Boolean },
      isLoadingAE: { type: Boolean },
      context: { type: Object },
      savedData: { type: Object },
      inValidAudienceDetails: { type: Boolean },
      groupingSegments: { type: Array },
    };
  }

  defaults = {
    audiences: [
      {
        uuid: null,
        name: null,
        populationCount: null,
        idCount: null,
        stagePercentages: [
          {
            value: null,
            invalid: '',
            group: '',
            roleForComms: {
              role: null,
              title: null,
              description: null,
              info: 50,
              interest: 50,
              need: 50,
            },
            customSegments: [],
          },
        ],
      },
    ],
    stages: [{ name: null, id: this.#now }],
  };

  /**
   * @description CSS styles for the component CampaignAudienceViz
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni div.box {
          display: flex;
          flex-flow: column nowrap;
          gap: 2.57rem;
          height: calc(100vh - 18.71rem);
          min-height: 42.22rem;
          padding: 1.43rem 2.857rem 3.214rem 2.857rem;
        }
        div.header-container {
          position: relative;
          display: flex;
          flex-flow: row nowrap;
          justify-content: flex-start;
          align-items: center;
          gap: 0.571rem;
          margin-left: -1.43rem;
          margin-top: 0.36rem;
        }
        div.header-container .button.is-text {
          color: var(--color-core-dark);
        }
        .omni div.header-container .button.is-active {
          background-image: linear-gradient(
            135deg,
            rgb(var(--rgb-primary), 0.75) 0,
            rgb(var(--rgb-secondary), 0.75) 100%
          );
          border-color: rgb(var(--rgb-primary), 0.75);
          color: var(--color-core-light);
        }
        omni-switch,
        div.header-container .button.is-outlined {
          margin-left: auto;
          margin-right: 5.74rem;
        }
        div.content-container {
          flex: 1 1 100%;
          display: flex;
          gap: 1.71rem;
          flex-direction: column;
          min-height: 0;
        }
        omni-loading-indicator {
          margin: auto 0;
        }
      `,
    ];
  }

  /**
   * @property {Object} tabs - Object containing the different views of the module
   * @property {Boolean} readOnly - Boolean that determines if the module is editable or not
   * @property {String} audienceView - String that determines the view of the module
   * @property {Object[]} audiences
   * @property {Object[]} stages
   * @property {String} funnelType - String that determines the type of funnel, either 'pipeline' or 'stacked'
   * @property {Boolean} isCluster - Boolean that determines if the funnel is a cluster or not
   * @property {Object} schema - Object that contains the schema of the audiences
   * @property {Boolean} showRFCLegend - Boolean that determines if the RFC legend is shown or not
   */
  constructor() {
    super();
    this.readOnly = this.context?.readOnly;
    this.tabs = {
      detail: 'Audience Details',
      viz: 'Audience Visualization',
      comms: 'Role for Comms',
    };
    this.audienceView = 'detail';
    this.audiences = this.defaults.audiences;
    this.stages = this.defaults.stages;
    this.groups = {};
    this.groupingSegments = [];
    this.funnelType = null;
    this.isCluster = true;
    this.showRFCLegend = false;
    this.showGroupSegments = false;
    this.selectedAEObject = [];
    this.inValidAudienceDetails = false;
  }

  get #now() {
    return new Date().getTime();
  }

  connectedCallback() {
    super.connectedCallback();
    const {
      audiences = this.defaults.audiences,
      stages = this.defaults.stages,
      funnelType = null,
      isCluster = true,
      groups = {},
    } = this.savedData;

    this.audiences = audiences;
    this.stages = stages;
    this.funnelType = funnelType;
    this.isCluster = isCluster;
    this.groups = groups;
  }

  firstUpdated() {
    if (!this.savedData || Object.keys(this.savedData).length === 0) {
      this.audienceView = 'detail';
    } else {
      this.audienceView = 'viz';
    }
  }

  updated(changedProps) {
    if (changedProps.has('funnelType')) {
      this.#validatePercentages();
    }
    if (changedProps.has('audiences') && this.audiences) {
      this.audiences.forEach((audience, index) => {
        if (audience.uuid) {
          this.#selectedAEObjects(audience, index);
        }
      });
    }
  }

  /**
   * @description Adds the selected Audiences to the selectedAEObject array when this.audiences is updated
   * @param {String} uuid - uuid of the selected audience object
   * @param {Number} audienceIndex - index of the audience dropdown
   */
  #selectedAEObjects = ({ uuid }, audienceIndex) => {
    this.selectedAEObject = produce(this.selectedAEObject, draft => {
      draft[audienceIndex] = uuid;
    });
  };

  /**
   * CampaignAudienceViz event to save data
   * @event CampaignAudiencePersona#omni-module-changed
   * @type {object}
   * @property {string} moduleId - The id of the module that changed
   * @property {Function} saveAction - The function to call when the step is saved
   */
  #assetChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submitModuleData.bind(this),
        validateAction: this.#validateSave.bind(this),
      },
    });
  }

  #validateSave() {
    if (this.inValidAudienceDetails) {
      om2alert('Invalid Audience Details', {
        type: 'danger',
        toast: true,
      });
      return false;
    }
    return true;
  }

  /**
   * Submits and saves the data to CampaignAPI under the correct campaign and module.
   * Save the data for the module as well as shares grouped and ungrouped segment data that has a role for comms to the shared fridge.
   * @async
   * @returns {Promise<void>}
   */
  async _submitModuleData() {
    const isPipeline = this.funnelType?.toLowerCase() === 'pipeline';

    const audienceSegment = this.audiences.reduce((acc, audience) => {
      const { uuid, name, populationCount, idCount } = audience;

      audience.stagePercentages.forEach((stage, index) => {
        if (this.#hasRoleForComms(stage)) {
          const stageName = this.#getStageName(stage, index);
          const stageId = this.#getStageID(stage, index);
          const percent = stage.value;
          const roleForComms = this.#hasRoleForComms(stage);

          const groupedSegment = acc.find(
            seg => seg.uuid === uuid && seg.stageName === stageName,
          );

          if (groupedSegment) {
            this.#updateGroupedSegment(groupedSegment, percent, isPipeline);
          } else {
            acc.push({
              roleForComms,
              stageName,
              stageId,
              populationCount,
              idCount,
              percent,
              uuid,
              name,
            });
          }
        }
      });

      return acc;
    }, []);

    const audienceVizData = {
      audiences: this.audiences,
      stages: this.stages,
      groups: this.groups,
      funnelType: this.funnelType,
      isCluster: this.isCluster,
    };

    await this.save(audienceVizData, {
      '/studio/audience_segments': audienceSegment,
    });
  }

  /**
   * Helper function that checks if the stage or its group has a role for communications.
   * @returns {boolean} - True if the stage or its group has a role for communications, otherwise false.
   */
  #hasRoleForComms(stage) {
    return (
      this.groups[stage.group]?.roleForComms?.role ?? stage.roleForComms.role
    );
  }

  /**
   * Helper function to gets the stage name based based if the segment is part of a group or not.
   * @returns {string} - The name of the stage.
   */
  #getStageName(stage, index) {
    return stage.group || this.stages[index].name;
  }

  /**
   * Helper function that gets the stageId based if the segment is part of a group or not.
   * @returns {string} - The id of the of the segment
   */
  #getStageID(stage, index) {
    return this.groups[stage.group]?.id ?? this.stages[index]?.id;
  }

  /**
   * Helper function that updates a grouped segment with the correct stageId and percent.
   */
  #updateGroupedSegment(segment, percent, isPipeline) {
    if (isPipeline) {
      if (percent > segment.percent) {
        segment.percent = percent;
      }
    } else {
      segment.percent += percent;
    }
  }

  /**
   * @description Adds a new audience to the audiences array
   */
  #addAudience = () => {
    this.audiences = produce(this.audiences, draft => {
      draft.push({
        uuid: null,
        name: null,
        populationCount: null,
        idCount: null,
        stagePercentages: this.stages.map(() => ({
          value: null,
          invalid: '',
          group: '',
          roleForComms: {
            role: null,
            title: null,
            description: null,
            info: 50,
            interest: 50,
            need: 50,
          },
          customSegments: [],
        })),
      });
    });
    this.#assetChange();
  };

  /**
   * @description Deletes an audience from the audiences array
   * @param {number} audienceIndex - index of the audience to delete
   */
  #deleteAudience = ({ detail: { audienceIndex } }) => {
    this.audiences = produce(this.audiences, draft => {
      draft.splice(audienceIndex, 1);
    });
    this.#assetChange();
  };

  /**
   * @description Handles the change of the audience selected
   * @param {number} audienceIndex - index of the audience to change
   * @param {number} value - UUID of the audience
   */
  #handleAudienceChange = ({ detail: { audienceIndex, value } }) => {
    const { name, uuid, data_points: dataPoints } = value;
    const { pop_id_count: populationCount, id_count: idCount } = dataPoints;
    this.audiences = produce(this.audiences, draft => {
      const audience = draft[audienceIndex];
      audience.name = name;
      audience.uuid = uuid;
      audience.populationCount = populationCount;
      audience.idCount = idCount;
    });
    this.#assetChange();
  };

  /**
   * @description Adds a new stage to the stages array. Also adds a new stage to the stagePercentages array of each audience
   */
  #addStage = () => {
    // If we're adding a second stage, default the funnelType
    if (this.stages.length === 1) {
      this.funnelType = 'pipeline';
    }

    this.stages = produce(this.stages, draft => {
      draft.push({ name: null, id: this.#now });
    });

    this.audiences = produce(this.audiences, draft => {
      draft.forEach(audience => {
        audience.stagePercentages.push({
          value: null,
          invalid: '',
          group: '',
          roleForComms: {
            role: null,
            title: null,
            description: null,
            info: 50,
            interest: 50,
            need: 50,
          },
          customSegments: [],
        });
      });
    });
    this.isCluster = this.stages.length === 1;
    this.#assetChange();
  };

  /**
   * @description Deletes a stage from the stages array. Also deletes the stage from the stagePercentages array of each audience. If there are less than 2 stages, the funnelType is set to null and the radio buttons are unchecked.
   * @param {number} stageIndex - index of the stage to delete
   */
  #deleteStage = async ({ detail: { stageIndex } }) => {
    this.stages = produce(this.stages, draft => {
      draft.splice(stageIndex, 1);
    });
    this.audiences = produce(this.audiences, draft => {
      draft.forEach(a => {
        a.stagePercentages.splice(stageIndex, 1);
      });
    });
    await this.updateComplete;
    this.isCluster = this.stages.length < 2;
    if (this.stages.length < 2) this.funnelType = null;
    this.#assetChange();
  };

  /**
   * @description Validates the percentages of the stages based on the funnel type
   */
  #validatePercentages = () => {
    if (this.funnelType === 'stacked') this.#validateStackedPercentages();
    if (this.funnelType === 'pipeline') this.#validatePipelinePercentages();
  };

  /**
   * @description Validates the percentages of the stages for a stacked funnel by adding a string warning to the invalid key of the stagePercentages object
   */
  #validateStackedPercentages = () => {
    this.audiences = produce(this.audiences, draft =>
      draft.map(({ stagePercentages, ...rest }) => {
        const valuesPresent = stagePercentages.every(value => value !== null);
        // validate only if all percent values to an audience are present
        if (valuesPresent) {
          const sum = stagePercentages.reduce(
            (acc, curr) => acc + curr.value,
            0,
          );
          // values are invalid if sum is not 100
          if (sum !== 100) {
            return {
              ...rest,
              stagePercentages: stagePercentages.map(p => ({
                ...p,
                invalid: 'Sum of percentages must equal 100',
              })),
            };
          }
          // remove invalid message if sum is 100
          if (sum === 100) {
            return {
              ...rest,
              stagePercentages: stagePercentages.map(p => ({
                ...p,
                invalid: '',
              })),
            };
          }
        }
        return { ...rest, stagePercentages };
      }),
    );
  };

  /**
   * @description Validates the percentages of the stages for a pipeline funnel by adding a string warning to the invalid key of the stagePercentages object
   */
  #validatePipelinePercentages = () => {
    this.audiences = produce(this.audiences, draft =>
      draft.map(({ stagePercentages, ...rest }) => {
        const updatedStagePercentages = stagePercentages.map(
          ({ value, invalid, ...spread }, i) => {
            if (value !== null) {
              // check if first stage is 100%
              if (i === 0 && value !== 100) {
                return {
                  value,
                  invalid: 'First stage must be 100%',
                  ...spread,
                };
              }
              // check if the following stages are less than the previous stage
              if (i > 0 && value >= stagePercentages[i - 1].value) {
                return {
                  value,
                  invalid: `Stage ${i + 1} must be less than Stage ${i}`,
                  ...spread,
                };
              }
            }
            return {
              value,
              invalid: '',
              ...spread,
            };
          },
        );
        return { ...rest, stagePercentages: updatedStagePercentages };
      }),
    );
  };

  /**
   * @description Handles the change of the percentage of a stage and checks if the value is a valid number
   * @param {number} percentIndex - index of the percentage to change
   * @param {number} audienceIndex - index of the audience the percentage belongs to
   * @param {number} value - percentage of the stage
   */
  #handlePercentageChange = ({
    detail: { percentIndex, audienceIndex, value },
  }) => {
    const regex = /^\d+(\.\d{1,2})?$/;
    if (!regex.test(value)) {
      this.audiences = produce(this.audiences, draft => {
        draft[audienceIndex].stagePercentages[percentIndex].invalid =
          'Please enter a valid number';
        draft[audienceIndex].stagePercentages[percentIndex].value = null;
      });
    } else {
      const newValue = parseInt(value, 10);
      this.audiences = produce(this.audiences, draft => {
        draft[audienceIndex].stagePercentages[percentIndex].invalid = '';
        draft[audienceIndex].stagePercentages[percentIndex].value = newValue;
      });
      this.#validatePercentages();
      this.#assetChange();
    }
  };

  /**
   * @description Handles the change of the stage name
   * @param {number} stageIndex - index of the stage to change
   * @param {string} value - name of the stage
   */
  #handleStageChange = ({ detail: { stageIndex, value } }) => {
    this.stages = produce(this.stages, draft => {
      draft[stageIndex].name = value;
    });
    this.#assetChange();
  };

  #onSwitchChange(e) {
    this.showRFCLegend = e.detail.checked;
  }

  #onCustomSegmentsChange = e => {
    e.stopPropagation();
    const {
      detail: { pinnedSegments, percentageIndex, audienceIndex, groupName },
    } = e;
    if (groupName) {
      this.groups = produce(this.groups, draft => {
        draft[groupName].customSegments = pinnedSegments;
      });
    } else {
      this.audiences = produce(this.audiences, draft => {
        draft[audienceIndex].stagePercentages[percentageIndex].customSegments =
          pinnedSegments;
      });
    }
    this.#assetChange();
  };

  #onRFCChange = e => {
    e.stopPropagation();
    const {
      detail: {
        rfc,
        rfcTitle,
        rfcDescription,
        info,
        interest,
        need,
        percentageIndex,
        audienceIndex,
        groupName,
      },
    } = e;
    if (groupName) {
      this.groups = produce(this.groups, draft => {
        const groupComms = draft[groupName].roleForComms;
        groupComms.role = rfc;
        groupComms.title = rfcTitle;
        groupComms.description = rfcDescription;
        groupComms.info = info;
        groupComms.interest = interest;
        groupComms.need = need;
      });
    } else {
      this.audiences = produce(this.audiences, draft => {
        const { roleForComms } =
          draft[audienceIndex].stagePercentages[percentageIndex];
        roleForComms.role = rfc;
        roleForComms.title = rfcTitle;
        roleForComms.description = rfcDescription;
        roleForComms.info = info;
        roleForComms.interest = interest;
        roleForComms.need = need;
      });
    }
    this.#assetChange();
  };

  /**
   *
   * @param {Event} e - event object from the omni-checkbox found in the funnel viz component
   * @description Handles the change of the segment grouping. Adds or removes the selected segment from the groupingSegments array
   */
  #onSegmentGroupingChange = e => {
    e.stopPropagation();
    const { audienceIndex, percentageIndex, checked } = e.detail;
    if (checked) {
      this.groupingSegments = produce(this.groupingSegments, draft => {
        draft.push({ audienceIndex, percentageIndex });
      });
    } else {
      this.groupingSegments = produce(this.groupingSegments, draft =>
        draft.filter(
          ({ audienceIndex: ai, percentageIndex: pi }) =>
            ai !== audienceIndex || pi !== percentageIndex,
        ),
      );
    }
  };

  #nameGroupSegments = e => {
    e.stopPropagation();
    const { name } = e.detail;
    this.audiences = produce(this.audiences, draft => {
      this.groupingSegments.forEach(({ audienceIndex, percentageIndex }) => {
        draft[audienceIndex].stagePercentages[percentageIndex].group = name;
      });
    });

    this.groups = produce(this.groups, draft => {
      draft[name] = {
        id: this.#now,
        roleForComms: {
          role: null,
          title: null,
          description: null,
          info: 50,
          interest: 50,
          need: 50,
        },
        customSegments: [],
      };

      this.groupingSegments.forEach(({ audienceIndex, percentageIndex }) => {
        const currentSegment =
          this.audiences[audienceIndex].stagePercentages[percentageIndex];
        if (currentSegment.roleForComms.role) {
          draft[name].roleForComms = { ...currentSegment.roleForComms };
        }
        draft[name].customSegments = [
          ...draft[name].customSegments,
          ...currentSegment.customSegments,
        ];
      });
    });

    this.groupingSegments = [];
    this.showGroupSegments = false;
    this.#assetChange();
  };

  #ungroupSegments = e => {
    e.stopPropagation();
    const name = e.detail;

    this.audiences = produce(this.audiences, draft => {
      draft.forEach(audience => {
        audience.stagePercentages.forEach(stage => {
          if (stage.group === name) {
            stage.group = '';
          }
        });
      });
    });

    this.groups = produce(this.groups, draft => {
      delete draft[name];
    });

    this.#assetChange();
  };

  #rfcLegendTemplate = () => html`
    ${this.showRFCLegend
      ? html` <campaign-audience-rfc-legend
          @close-rfc-legend=${() => {
            this.showRFCLegend = false;
          }}
        ></campaign-audience-rfc-legend>`
      : nothing}
  `;

  #groupSegmentsTemplate = () => html`
    ${this.showGroupSegments
      ? html` <campaign-audience-segment-grouping
          @close-group-segments=${() => {
            this.showGroupSegments = false;
          }}
          @name-group-segments=${this.#nameGroupSegments}
        ></campaign-audience-segment-grouping>`
      : nothing}
  `;

  /**
   * @returns {TemplateResult} - HTML template for the modules tabs that allow the user to switch between the different views of the module
   */
  #audienceTabsTemplate = () => html`
    ${map(
      Object.keys(this.tabs),
      key => html`
        <button
          ?disabled=${this.inValidAudienceDetails &&
          (key === 'viz' || key === 'comms')}
          id="${key}-tab"
          class="button is-text ${this.audienceView === key ? 'is-active' : ''}"
          @click=${() => {
            this.audienceView = key;
          }}
        >
          ${this.tabs[key]}
        </button>
      `,
    )}
    ${this.audienceView === 'comms'
      ? html`
          <omni-switch
            .checked=${this.showRFCLegend}
            @change=${this.#onSwitchChange}
          >
            <span>View Legend</span>
          </omni-switch>
        `
      : nothing}
    ${this.audienceView === 'viz'
      ? html`
          <button
            class="button is-outlined"
            ?disabled="${this.groupingSegments.length < 2}"
            @click="${() => {
              this.showGroupSegments = true;
            }}"
          >
            Group
          </button>
        `
      : nothing}
  `;

  /**
   * @returns {TemplateResult} - HTML template for the module view selected by the user
   */
  #audienceViewTemplate = () => {
    switch (this.audienceView) {
      case 'detail':
        return html`<campaign-audience-viz-detail-view
          .token=${this.token}
          .env=${this.env}
          .audiences=${this.audiences}
          .stages=${this.stages}
          .schema=${this.schema}
          .funnelType=${this.funnelType}
          .isCluster=${this.isCluster}
          .readOnly=${this.readOnly}
          .context=${this.context}
          .selectedAEObject=${this.selectedAEObject}
          .inValidAudienceDetails=${this.inValidAudienceDetails}
          @add-audience=${this.#addAudience}
          @delete-audience=${this.#deleteAudience}
          @add-stage=${this.#addStage}
          @delete-stage=${this.#deleteStage}
          @audience-change=${this.#handleAudienceChange}
          @stage-change=${this.#handleStageChange}
          @percentage-change=${this.#handlePercentageChange}
          @radio-change=${({ detail: { value } }) => {
            this.funnelType = value;
            this.#assetChange();
          }}
          @cluster-change=${({ detail: { isCluster } }) => {
            this.isCluster = isCluster;
          }}
          @create-visualization=${() => {
            this.audienceView = 'viz';
          }}
        >
        </campaign-audience-viz-detail-view>`;
      case 'viz':
        if (this.isCluster)
          return html` <campaign-audience-cluster-viz
            .context=${this.context}
            .token=${this.token}
            .env=${this.env}
            .audiences=${this.audiences}
            .stages=${this.stages}
            .audienceView=${this.audienceView}
            .selectedAEObject=${this.selectedAEObject}
            @update-custom-segments=${this.#onCustomSegmentsChange}
          >
          </campaign-audience-cluster-viz>`;
        return html` <campaign-audience-funnel-viz
          .context=${this.context}
          .token=${this.token}
          .env=${this.env}
          .audiences=${this.audiences}
          .stages=${this.stages}
          .groups=${this.groups}
          .groupingSegments=${this.groupingSegments}
          .funnelType=${this.funnelType}
          .audienceView=${this.audienceView}
          .selectedAEObject=${this.selectedAEObject}
          @update-custom-segments=${this.#onCustomSegmentsChange}
          @group-segments=${this.#onSegmentGroupingChange}
          @ungroup-segments=${this.#ungroupSegments}
        ></campaign-audience-funnel-viz>`;
      case 'comms':
        if (this.isCluster)
          return html` <campaign-audience-cluster-viz
            .context=${this.context}
            .token=${this.token}
            .env=${this.env}
            .audiences=${this.audiences}
            .stages=${this.stages}
            .audienceView=${this.audienceView}
            .selectedAEObject=${this.selectedAEObject}
            @save-rfc-sliders=${this.#onRFCChange}
          >
          </campaign-audience-cluster-viz>`;
        return html`<campaign-audience-funnel-viz
          .context=${this.context}
          .token=${this.token}
          .env=${this.env}
          .audiences=${this.audiences}
          .stages=${this.stages}
          .groups=${this.groups}
          .funnelType=${this.funnelType}
          .audienceView=${this.audienceView}
          .selectedAEObject=${this.selectedAEObject}
          @save-rfc-sliders=${this.#onRFCChange}
        ></campaign-audience-funnel-viz>`;
      default:
        return nothing;
    }
  };

  /**
   * @returns {TemplateResult} - HTML template for the audience viz module
   */
  render() {
    const audienceCheck = this.audiences.some(a => a.uuid === null);
    const stageCheck = this.stages.some(s => s.name === null || s.name === '');
    const percentageCheck = this.audiences.some(a =>
      a.stagePercentages.some(p => p.invalid || p.value === null),
    );
    this.inValidAudienceDetails =
      audienceCheck || stageCheck || percentageCheck;
    return html`
      <omni-style>
        <div class="box">
          <div class="header-container">
            ${this.#audienceTabsTemplate()} ${this.#rfcLegendTemplate()}
            ${this.#groupSegmentsTemplate()}
          </div>
          <div class="content-container">${this.#audienceViewTemplate()}</div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-audience-viz', CampaignAudienceViz);
